import { initializeApp, getApps } from "firebase/app";
import { getMessaging, getToken, onMessage } from "firebase/messaging";

const FCM_TOKEN_STORAGE_KEY = "fcm_token";

const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
  measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase (avoid re-initializing on hot reload)
const app =
  getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];

const isBrowser = () => typeof window !== "undefined";

const isMessagingSupported = () =>
  isBrowser() &&
  "Notification" in window &&
  "serviceWorker" in navigator;

export const getStoredFCMToken = () => {
  if (!isBrowser()) return null;

  return window.localStorage.getItem(FCM_TOKEN_STORAGE_KEY);
};

const storeFCMToken = (token) => {
  if (!isBrowser()) return;

  if (token) {
    window.localStorage.setItem(FCM_TOKEN_STORAGE_KEY, token);
    return;
  }

  window.localStorage.removeItem(FCM_TOKEN_STORAGE_KEY);
};

const waitForActiveServiceWorker = async (registration) => {
  if (registration.active) {
    return registration;
  }

  await navigator.serviceWorker.ready;

  if (registration.active) {
    return registration;
  }

  await new Promise((resolve, reject) => {
    const worker = registration.installing || registration.waiting;

    if (!worker) {
      reject(new Error("Service worker did not enter an installable state."));
      return;
    }

    const handleStateChange = () => {
      if (worker.state === "activated") {
        worker.removeEventListener("statechange", handleStateChange);
        resolve();
      }
    };

    worker.addEventListener("statechange", handleStateChange);
  });

  return registration;
};

/**
 * Request notification permission and return the FCM token.
 * Returns null if permission is denied or if running on server.
 */
export const getFCMToken = async () => {
  if (!isMessagingSupported()) return null;

  try {
    if (Notification.permission === "denied") {
      console.warn("Notification permission has been denied.");
      storeFCMToken(null);
      return null;
    }

    const permission =
      Notification.permission === "granted"
        ? "granted"
        : await Notification.requestPermission();

    if (permission !== "granted") {
      console.warn("Notification permission not granted.");
      storeFCMToken(null);
      return null;
    }

    const messaging = getMessaging(app);

    const existingRegistration =
      await navigator.serviceWorker.getRegistration("/firebase-messaging-sw.js");
    const registration =
      existingRegistration ||
      (await navigator.serviceWorker.register("/firebase-messaging-sw.js"));
    await waitForActiveServiceWorker(registration);

    const token = await getToken(messaging, {
      vapidKey: process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: registration,
    });

    if (token) {
      storeFCMToken(token);
      console.log("FCM Token:", token);
      return token;
    } else {
      storeFCMToken(null);
      console.warn("No FCM token received.");
      return null;
    }
  } catch (error) {
    storeFCMToken(null);
    console.error("Error getting FCM token:", error);
    return null;
  }
};

/**
 * Show an OS-level browser notification for foreground messages.
 * Firebase only auto-shows notifications when the tab is in the background;
 * for foreground we must call this manually.
 */
export const showForegroundNotification = (payload) => {
  if (!isMessagingSupported()) return;
  if (Notification.permission !== "granted") return;

  const title = payload?.notification?.title || "New Notification";
  const options = {
    body: payload?.notification?.body || "",
    icon: "/favicon.svg",
    badge: "/favicon.svg",
    data: payload?.data || {},
  };

  // Use the service worker registration to show the notification
  // so it works consistently across browsers and triggers notificationclick
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.ready.then((registration) => {
      registration.showNotification(title, options);
    });
  } else {
    // Fallback to basic Notification API
    const notification = new Notification(title, options);
    notification.onclick = () => {
      window.focus();
      const data = payload?.data || {};
      const notificationType = data.type || "";
      const conversationId = data.conversationId || data.conversation_id || null;
      if (
        notificationType === "NEW_MESSAGE" ||
        notificationType === "MESSAGE" ||
        notificationType === "MESSAGE_RECEIVED" ||
        notificationType.toLowerCase().includes("message")
      ) {
        const targetUrl = conversationId
          ? `/messages?conversationId=${encodeURIComponent(conversationId)}`
          : "/messages";
        window.location.href = targetUrl;
      }
    };
  }
};

/**
 * Listen for foreground FCM messages.
 * Returns a Promise that resolves with the message payload.
 */
export const onMessageListener = () => {
  if (!isMessagingSupported()) return Promise.resolve(null);

  return new Promise((resolve) => {
    const messaging = getMessaging(app);
    onMessage(messaging, (payload) => {
      resolve(payload);
    });
  });
};

export const subscribeToForegroundMessages = (callback) => {
  if (!isMessagingSupported()) {
    return () => {};
  }

  const messaging = getMessaging(app);

  return onMessage(messaging, (payload) => {
    callback(payload);
  });
};

export { app };
